<template>
  <form-carousel v-if="!onboardingStore.draftMerchant" ref="carouselControls" hide-progress>
    <!-- Field 1: shortcode -->
    <form-carousel-item
      name="invite"
      :disable-next-if="v$.shortcode.$invalid"
      :label="t('onboarding.auth.shortcode_label')"
      @focus="shortcodeFieldRef?.focus"
      @next="submit"
    >
      <v-otp-input
        ref="shortcodeFieldRef"
        v-model="form.shortcode"
        length="6"
        autofocus
        input-classes="otp-input"
        variant="outlined"
        base-color="purple-4"
        color="background"
        class="justify-start"
        style="margin-left: -8px"
        :error-messages="validationErrors(v$.shortcode.$errors)"
        @blur="v$.shortcode.$touch"
      />
    </form-carousel-item>
  </form-carousel>
  <onboarding-sign-up v-else />
</template>

<script setup lang="ts">
import useVuelidate from "@vuelidate/core";
import { helpers, maxLength, minLength, numeric, required } from "@vuelidate/validators";
import { reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { VOtpInput } from "vuetify/components";
import FormCarousel from "../Form/FormCarousel.vue";
import FormCarouselItem from "../Form/FormCarouselItem.vue";
import { OnboardingError, useOnboardingStore } from "@/store/onboarding";
import { validationErrors } from "@/utils/format";

const { t } = useI18n();
const shortcodeFieldRef = ref<typeof VOtpInput | null>(null);
const carouselControls = ref<InstanceType<typeof FormCarousel> | null>(null);
const onboardingStore = useOnboardingStore();

const form = reactive({
  shortcode: "",
  email: "",
});

const rules = {
  shortcode: {
    required: helpers.withMessage(t("onboarding.auth.shortcode_required"), required),
    numeric: helpers.withMessage(t("onboarding.auth.shortcode_numeric"), numeric),
    minLength: helpers.withMessage(t("onboarding.auth.shortcode_min_length"), minLength(6)),
    maxLength: helpers.withMessage(t("onboarding.auth.shortcode_max_length"), maxLength(6)),
  },
};

const v$ = useVuelidate(rules, form);

const submitting = ref<boolean>(false);

const route = useRoute();
const inviteToken = route.query.invite as string;

const submit = async () => {
  carouselControls.value?.clearError();
  v$.value.$touch();
  if (v$.value.$invalid) {
    carouselControls.value?.setError(
      v$.value.$errors[0]?.$message.toString() || t("form.error_invalid_fields"),
    );
    return;
  }

  try {
    carouselControls.value?.setError("");
    submitting.value = true;
    const draftMerchant = await onboardingStore.getDraftMerchantFromInvite(
      form.shortcode,
      inviteToken,
    );
    if (!draftMerchant) {
      carouselControls.value?.setError(t("form.error_something_went_wrong"));
    }
  } catch (error) {
    if (error instanceof OnboardingError) {
      carouselControls.value?.setError(error.message, error.field ?? "invite");
    } else {
      carouselControls.value?.setError(t("form.error_something_went_wrong"), "invite");
    }
  } finally {
    submitting.value = false;
  }
};
</script>
